// The board's half of the widget kit: it builds the sealed document a custom
// widget runs in, with the board's fonts, theme and data already inside, so
// the first frame it paints is the finished widget. runtime.js is the half that
// runs inside; they speak through postMessage.

import RUNTIME from "./runtime.js?raw";
import KIT_CSS from "./kit.css?raw";
import geistUrl from "@fontsource-variable/geist/files/geist-latin-wght-normal.woff2?url";
import serifUrl from "@fontsource/instrument-serif/files/instrument-serif-latin-400-normal.woff2?url";
import pixelUrl from "@fontsource-variable/pixelify-sans/files/pixelify-sans-latin-wght-normal.woff2?url";
import monoUrl from "@fontsource-variable/jetbrains-mono/files/jetbrains-mono-latin-wght-normal.woff2?url";
import type { Stream } from "../../../shared/types";

/**
 * Nothing may leave: no fetch, no images or fonts from anywhere, no frames,
 * and every string that becomes HTML goes through the kit's own policy
 * (Trusted Types), which keeps a widget from making frames of its own.
 */
export const CSP = "default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src data: blob:; font-src data:; media-src data: blob:; require-trusted-types-for 'script'; trusted-types default";

/** A widget's own frames would run without the kit, so their tags are disarmed before it ever loads. */
const FRAMES = /<(\s*\/?\s*)(iframe|frame|frameset|object|embed|portal|fencedframe)\b/gi;
export const disarm = (html: string) => html.replace(FRAMES, (_m, pre: string, tag: string) => `<${pre}cb-blocked-${tag.toLowerCase()}`);

const CSS = KIT_CSS.replace(/\/\*[\s\S]*?\*\//g, "").replace(/\s+/g, " ").trim();

const VARS = [
  "--fg", "--fg-2", "--fg-3", "--fg-4",
  "--accent", "--accent-fg", "--accent-soft", "--accent-line",
  "--ok", "--warn", "--bad", "--ok-soft", "--warn-soft", "--bad-soft",
  "--chart-1", "--chart-2", "--chart-3", "--chart-4", "--chart-5",
  "--line", "--line-2", "--field", "--hover", "--press",
  "--radius", "--radius-sm",
  "--font-display", "--font-num", "--num-weight", "--num-tracking", "--font-num-sm", "--num-sm-weight",
  "--tile-rgb", "--chrome-rgb", "--chrome-border",
  "--scroll-thumb", "--scroll-thumb-hover",
];

export type Voice = "clean" | "editorial" | "pixel" | "mono";

export interface KitTheme {
  vars: Record<string, string>;
  /** board.theme inside the widget: the same colours as values, for canvas and SVG code */
  theme: Record<string, unknown>;
  voice: Voice;
}

const camel = (k: string) => k.replace(/^--/, "").replace(/-(\w)/g, (_, c: string) => c.toUpperCase());

/** The theme as the widget's own tile has it, a per-widget colour included. */
export function readTheme(from: Element | null): KitTheme {
  const el = from ?? document.querySelector(".board-root");
  const vars: Record<string, string> = {};
  const theme: Record<string, unknown> = {};
  if (el) {
    const cs = getComputedStyle(el);
    for (const k of VARS) {
      const v = cs.getPropertyValue(k).trim();
      if (!v) continue;
      vars[k] = v;
      theme[camel(k)] = v;
    }
  }
  const mode = (el?.closest("[data-mode]") as HTMLElement | null)?.dataset.mode === "light" ? "light" : "dark";
  theme.mode = mode;
  theme.charts = [1, 2, 3, 4, 5].map((i) => vars[`--chart-${i}`]).filter(Boolean);
  if (vars["--tile-rgb"]) theme.tile = `rgb(${vars["--tile-rgb"]})`;
  theme.font = vars["--font-display"] ?? "";
  const display = vars["--font-display"] ?? "";
  const voice: Voice = /Instrument Serif/i.test(display) ? "editorial" : /Pixelify/i.test(display) ? "pixel" : /JetBrains/i.test(display) ? "mono" : "clean";
  return { vars, theme, voice };
}

// ---------------------------------------------------------------------------
// fonts, read once from the files the board already loaded and kept as data

const FACES: Record<Voice, { family: string; url: string; weight: string }[]> = {
  clean: [],
  editorial: [{ family: "Instrument Serif", url: serifUrl, weight: "400" }],
  pixel: [{ family: "Pixelify Sans Variable", url: pixelUrl, weight: "400 700" }],
  mono: [{ family: "JetBrains Mono Variable", url: monoUrl, weight: "100 800" }],
};
const GEIST = { family: "Geist Variable", url: geistUrl, weight: "100 900" };

const asData = new Map<string, Promise<string>>();
function dataUrl(url: string): Promise<string> {
  let p = asData.get(url);
  if (!p) {
    p = fetch(url)
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(String(r.status)))))
      .then(
        (b) =>
          new Promise<string>((resolve, reject) => {
            const fr = new FileReader();
            fr.onload = () => resolve(String(fr.result).replace(/^data:[^;,]*/, "data:font/woff2"));
            fr.onerror = () => reject(fr.error);
            fr.readAsDataURL(b);
          }),
      )
      // without the font the widget still draws, in the system's sans
      .catch(() => "");
    asData.set(url, p);
  }
  return p;
}

const faceCss = new Map<Voice, string>();

/** The @font-face rules for a voice, or null while they load. */
export function fontsNow(voice: Voice): string | null {
  return faceCss.get(voice) ?? null;
}

export async function loadFonts(voice: Voice): Promise<string> {
  const have = faceCss.get(voice);
  if (have !== undefined) return have;
  const faces = [GEIST, ...FACES[voice]];
  const urls = await Promise.all(faces.map((f) => dataUrl(f.url)));
  const css = faces
    .map((f, i) => (urls[i] ? `@font-face{font-family:"${f.family}";font-style:normal;font-display:block;font-weight:${f.weight};src:url(${urls[i]}) format("woff2")}` : ""))
    .join("");
  faceCss.set(voice, css);
  return css;
}

// ---------------------------------------------------------------------------
// the document

/** What a widget gets as board.data: the stream, without the board's bookkeeping. */
export function payload(s: Stream | null) {
  if (!s) return null;
  return {
    value: s.value,
    label: s.label,
    format: s.format,
    points: s.points.length > 3000 ? s.points.slice(-3000) : s.points,
    items: s.items,
    rows: s.rows,
    columns: s.columns,
    text: s.text,
    json: s.json,
    updatedAt: s.updatedAt,
    // a source read for a period: {days, from, to, previous}, value being its total
    period: s.period ?? null,
  };
}

const json = (x: unknown) => JSON.stringify(x ?? null).replace(/</g, "\\u003c");

export interface SrcdocOpts {
  look: KitTheme;
  fonts: string;
  data: unknown;
  state: unknown;
  readOnly: boolean;
  header: boolean;
  /** a Custom board's records, as board.records starts with them */
  records?: unknown[];
}

const attr = (s: string) => s.replace(/&/g, "&amp;").replace(/"/g, "&quot;");

/**
 * The frame on the board. The widget runs one frame further in, because a
 * frame can always navigate itself, and only the policy of the page holding
 * it can stop that: this middle page's policy blocks every navigation, so
 * the widget cannot carry data out in an address either. Neither page can
 * reach the board, and the widget sees only what the board sends it.
 */
export function buildSrcdoc(html: string, o: SrcdocOpts): string {
  const scheme = o.look.theme.mode === "light" ? "light" : "dark";
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${CSP}"><style>:root{color-scheme:${scheme}}html,body{margin:0;height:100%;overflow:hidden;background:transparent}iframe{display:block;border:0;width:100%;height:100%;color-scheme:${scheme}}</style></head><body><iframe sandbox="allow-scripts" title="Widget" srcdoc="${attr(widgetDoc(html, o))}"></iframe></body></html>`;
}

/**
 * The widget's own document. The kit goes first, in the head, and the
 * runtime is told how many lines it took so an error's line number points at
 * the widget's own code.
 */
export function widgetDoc(html: string, o: SrcdocOpts): string {
  const vars = Object.entries(o.look.vars)
    .map(([k, v]) => `${k}:${v};`)
    .join("");
  const head = (off: number) =>
    `<meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${CSP}"><meta name="viewport" content="width=device-width"><style>${o.fonts}</style><style id="cb-vars">:root{${vars}}</style><style>${CSS}</style>` +
    `<script>window.__cb=${json({ off, theme: o.look.theme, data: o.data, state: o.state ?? {}, readOnly: o.readOnly, header: o.header, ...(o.records ? { records: o.records } : {}) })}</script><script>${RUNTIME}</script>`;
  const off = (head(0).match(/\n/g) ?? []).length;
  const boot = head(off);
  // The kit and its policy always come first, whatever the widget's html
  // starts with. A widget's own <html>, <head> or <body> tags still work: the
  // parser folds them into these.
  return `<!doctype html><html><head>${boot}</head><body>${disarm(html.replace(/^\s*<!doctype[^>]*>/i, ""))}</body></html>`;
}
